import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import TextInputModal from "../../../common/TextInputModal";
import { CheckBoxInput, SelectForm } from "../../../common/Buttons";
import { Divs } from "../../../common/StyledComponents";
import { FcCheckmark } from "react-icons/fc";
import ShipmentAdditionalServices from "../../../common/GlobalModals/Orders/ShipmentAdditionalServicesReturn";
import RowsInShipmentReturn from "../../../common/GlobalModals/Orders/editModal/RowsInShipmentReturn";
import {
  availableCountries,
  handleChangeOrdertypeGlobal,
  onChangeCheckBoxDeliveryGlobal,
  renderTextInBlock,
  shipmentAdditionalServicesAttributesParams,
  shipment_type_setter,
} from "../../../../helpers/OrdersFunctionsGlobal";
import { ClassicButton } from "../../../common/ButtonWithLoading";
import { useDispatch, useSelector } from "react-redux";
import { postOrderShipping } from "../../../../redux/Actions/orderAction";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { shipment_types_options } from "../../../../helpers/GlobalOptions";
import { antdMessageError } from "../../../../helpers/Utils";
import { DatePicker } from "antd";
import { shipmentTypeRules } from "../../../../helpers/ShipmentRules";
import { ButtonSecondary } from "../../../common";
import AntdConfirm from "../../../../helpers/AntdConfirm";

const defaultGeneral = {
  weight: 1,
  pack: 1,
  order_return: false,
  cod: false,
  cod_amount: 0,
  deliveryOnSaturday: false,
  delivery_same_day: false,
  sendingDocuments: false,
  who: "",
  deliveryTime: false,
  deliveryTimeHours: ["", ""],
  carrying: false,
  swap: false,
  lp_express_sizes: undefined,
  suppliers: undefined,
  delivery_after_18: false,
};
const defaultAddress = {
  parcel_terminal_id: undefined,
  firstname: "",
  lastname: "",
  postal_code: "",
  street: "",
  city: "",
  phone: "",
  other: "",
  comment: "",
};

const RETURN_COURIER_OPTIONS = ["dpd", "omniva", "venipak", "lp_express", "itella"];

const RETURN_PICKUP_TYPE_RULES = {
  dpd: ["courier", "terminal"],
  omniva: ["terminal"],
  default: ["courier"],
};

const isTerminalDelivery = (deliveryValue = "") =>
  deliveryValue.includes("_postMachine");

const getAllowedPickupTypes = (carrierType) =>
  RETURN_PICKUP_TYPE_RULES[carrierType] || RETURN_PICKUP_TYPE_RULES.default;

const getAllowedReturnDeliveries = (carrierType) => {
  const rules = shipmentTypeRules(carrierType);
  return rules.deliveries.filter(({ value }) => !isTerminalDelivery(value));
};

const getDefaultReturnPickupType = (carrierType) => {
  const allowedPickupTypes = getAllowedPickupTypes(carrierType);

  if (!allowedPickupTypes.length) {
    return "courier";
  }

  if (allowedPickupTypes.includes("terminal")) {
    return "terminal";
  }

  return allowedPickupTypes[0];
};

const normalizeReturnPickupType = (carrierType, candidatePickupType) => {
  const allowedPickupTypes = getAllowedPickupTypes(carrierType);

  if (allowedPickupTypes.includes(candidatePickupType)) {
    return candidatePickupType;
  }

  return getDefaultReturnPickupType(carrierType);
};

const getDefaultReturnDelivery = (carrierType) => {
  const allowedDeliveries = getAllowedReturnDeliveries(carrierType);
  if (!allowedDeliveries.length) {
    return shipmentTypeRules(carrierType).deliveries?.[0]?.value || false;
  }

  if (getAllowedPickupTypes(carrierType).includes("terminal")) {
    const terminalOption = allowedDeliveries.find(({ value }) =>
      isTerminalDelivery(value)
    );

    if (terminalOption) {
      return terminalOption.value;
    }
  }

  const courierOption = allowedDeliveries.find(
    ({ value }) => !isTerminalDelivery(value)
  );
  return courierOption?.value || allowedDeliveries[0].value;
};

const normalizeReturnDelivery = (carrierType, candidateDelivery) => {
  const allowedDeliveries = getAllowedReturnDeliveries(carrierType).map(
    ({ value }) => value
  );

  if (allowedDeliveries.includes(candidateDelivery)) {
    return candidateDelivery;
  }

  return getDefaultReturnDelivery(carrierType);
};

export default function ShipmentComponentReturn({
  order_rows,
  id,
  id_order,
  shipping_address,
  total_price,
  carrier,
  onClose,
  billing_address,
  shipments,
  cod,
  leasing_cod,
  leasing_advance,
  waiting_payments,
  order_shop,
  callBackOnCreate = () => console.log("created"),
  orderRowsChange,
  form_type = "Order",
  total_shipping_tax_incl = 0,
  multiple,
  refetch,
  isLoading,
  remark,
  pickup_address_id = null,
}) {
  const dispatch = useDispatch();
  const { pickup_addresses } = useSelector((state) => state.options);
  const or_quantity_manual = useSelector(
    (state) => state.orders.or_quantity_manual
  );

  const [action, setAction] = useState("creating");
  const [activeKey, setActiveKey] = useState([1, 2]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("dpd");
  const [delivery, setDelivery] = useState("dpd_b2c");
  const [pickupType, setPickupType] = useState(getDefaultReturnPickupType("dpd"));
  const [address, setAddress] = useState({
    ...defaultAddress,
    ...shipping_address,
    comment: remark || shipping_address?.comment || defaultAddress.comment,
  });
  const [general, setGeneral] = useState({
    ...defaultGeneral,
    suppliers: pickup_address_id || undefined,
  });
  const [errors, setErrors] = useState({});
  const [isSkipLabel, setIsSkipLabel] = useState(false);
  const [pickupDate, setPickupDate] = useState(null);
  const isMounted = useRef(true);
  const pickupTypeOptions = useMemo(
    () => getAllowedPickupTypes(type),
    [type]
  );

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (pickupType === "terminal") {
      setPickupDate(null);
    }
  }, [pickupType]);

  const onChangeAddress = useCallback((e, mass) => {
    if (mass) {
      return setAddress((prev) => ({ ...prev, ...e }));
    }
    const { value, name } = e.target;
    setAddress((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onChangeGeneral = useCallback((e) => {
    const { value, name } = e.target;
    setGeneral((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const onChangeType = useCallback((new_type) => {
    const finalType = new_type === "posti" ? "itella" : new_type;

    setType((prevType) => {
      setAddress((prevAddress) => {
        const {
          cod,
          delivery,
          deliveryOnSaturday,
          delivery_same_day,
          deliveryTime,
          deliveryTimeHours,
          pack,
          sendingDocuments,
          shipping_address_edit,
          swap,
          terminalSelected,
          weight,
          who,
        } = handleChangeOrdertypeGlobal(
          finalType,
          prevType,
          { ...shipping_address, comment: prevAddress.comment, parcel_terminal_id: null },
          { ...billing_address, comment: prevAddress.comment },
          false,
          carrier
        );

        const normalizedDelivery = normalizeReturnDelivery(finalType, delivery);
        const normalizedPickupType = normalizeReturnPickupType(
          finalType,
          pickupType
        );

        setGeneral((prev) => ({
          ...prev,
          cod,
          deliveryOnSaturday,
          delivery_same_day,
          deliveryTime,
          deliveryTimeHours,
          pack,
          sendingDocuments,
          swap,
          weight,
          who,
        }));
        setPickupType(normalizedPickupType);
        setDelivery(normalizedDelivery);

        return {
          ...shipping_address_edit,
          parcel_terminal_id: null,
        };
      });

      return finalType;
    });
  }, [shipping_address, billing_address, carrier, pickupType]);

  const onChangePickupType = useCallback((name) => {
    if (name === pickupType) return;

    const allowedPickupTypes = getAllowedPickupTypes(type);
    if (!allowedPickupTypes.includes(name)) return;

    const {
      pack,
      swap,
    } = onChangeCheckBoxDeliveryGlobal({ target: { name: delivery } }, delivery);

    setAddress((prev) => ({
      ...prev,
      parcel_terminal_id: null,
    }));
    setGeneral((prev) => ({
      ...prev,
      pack,
      swap,
    }));
    setPickupType(name);
  }, [delivery, pickupType, type]);

  const createShippingApi = useCallback(async (orderVariables) => {
    try {
      const res = await dispatch(
        postOrderShipping(id, orderVariables, "OrderReturn")
      );

      if (!isMounted.current) return;

      if (res.success && isMounted.current) {
        setErrors({});
        if (!multiple) {
          setAction("created");
          setTimeout(() => {
            if (isMounted.current) {
              setAction("creating");
            }
          }, 500);
        }
        callBackOnCreate(id, res.data);
      }
    } catch (err) {
      console.log("createShippingApi: ", err);
    } finally {
      await refetch();
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [dispatch, id, multiple, callBackOnCreate, refetch]);

  const createShipping = useCallback(async () => {
    if (order_shop?.identifier == "TCH" && type == "at_the_store") {
      return antdMessageError(
        "[TCH] Cannot be created without a product selected"
      );
    }

    if (!general.suppliers) {
      return antdMessageError("Please select a return address");
    }

    if (isMounted.current) {
      setLoading(true);
    }

    const { weight, pack: packets, suppliers } = general;
    const {
      parcel_terminal_id,
      firstname,
      lastname,
      postal_code,
      street,
      country_id,
      country,
      city,
      phone,
      other,
      comment,
    } = address;

    let orderVariables = {
      id_order,
      order_type: "OrderReturn",
      tr_request_item_ids: Array.isArray(multiple)
        ? multiple
          ?.filter(
            (multi) =>
              order_rows?.filter((e) => multi.order_row_id == e.id)?.length > 0
          )
          ?.map((d) => d.tr_request_item_id)
        : null,
      order_ids: Array.isArray(multiple)
        ? multiple
          ?.filter(
            (multi) =>
              order_rows?.filter((e) => multi.order_row_id == e.id)?.length > 0
          )
          ?.map((d) => d.order_id)
        : null,
      shipment: {
        shipment_type: shipment_type_setter(type, delivery),
        is_terminal_return: pickupType === "terminal",
        packets: packets || 1,
        weight: weight,
        pickup_address_id: suppliers,
        skip_labels: isSkipLabel,
        call_time_from:
          pickupType !== "terminal" && pickupDate
            ? pickupDate.format("YYYY-MM-DD")
            : null,
        shipment_address_attributes: {
          company: "",
          name: firstname + " " + lastname,
          address: street,
          country_id,
          city,
          postal_code,
          parcel_terminal_id,
          phone: phone?.trim(),
          email: other,
          comment,
        },
        shipment_order_rows_attributes: order_rows?.map((e) => ({
          quantity: or_quantity_manual[e.id] || +e?.quantity,
          title: e.product_name,
          price: e.unit_price_tax_incl,
          order_row_id: e.id,
        })) || [],
        shipment_additional_services_attributes:
          shipmentAdditionalServicesAttributesParams({
            ...general,
            packets: packets || 1,
            delivery,
            type,
          }),
      },
    };

    if (availableCountries(country_id, order_shop.country_id)) {
      return createShippingApi(orderVariables);
    }
    AntdConfirm({
      title: (
        <span>
          Are you sure you want to ship to this country:{" "}
          <b>{country?.en_name}</b>?
        </span>
      ),
      onOk: () => createShippingApi(orderVariables),
      onCancel: () => setLoading(false),
    });
  }, [
    order_shop, type, general, address, id_order, multiple, order_rows,
    or_quantity_manual, delivery, isSkipLabel, pickupDate, createShippingApi,
  ]);

  const createNewShipping = useCallback(() => {
    setAction("creating");
    setActiveKey([1, 2]);
    setType("dpd");
    setDelivery("dpd_b2c");
    setPickupType(getDefaultReturnPickupType("dpd"));
    setAddress(defaultAddress);
    setGeneral(defaultGeneral);
    setErrors({});
    setLoading(false);
  }, []);

  const collapseBlock = useCallback((item) => {
    setActiveKey((prev) => {
      if (prev.includes(item)) {
        return prev.filter((e) => e != item);
      }
      return [...prev, item];
    });
  }, []);

  const orderMemo = useMemo(() => ({ id }), [id]);
  const codDetailsMemo = useMemo(() => ({
    waiting_payments,
    default_cod: leasing_cod ? leasing_advance : total_price || 0,
  }), [waiting_payments, leasing_cod, leasing_advance, total_price]);

  if (action == "created") {
    return <SuccessCreated createNewShipping={createNewShipping} />;
  }

  return (
    <>
      <div
        className="py-2"
        style={{
          width: 400,
          backgroundColor: "#fff",
          border: "1px solid #dfdfdf",
        }}
      >
        <div className="col-12">
          <span className="font-weight-bold d-block modal-title text-orange mb-2">
            <span className="font-weight-bold text-black small">Shipments</span>
            <button type="button" className="close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
          </span>
        </div>
        <div className="col-12">
          <div className="d-flex flex-wrap minus-margin">
            {shipment_types_options.filter(
              option => RETURN_COURIER_OPTIONS.includes(option.value)
            ).map((e) => (
              <div
                key={e.value}
                className={`d-flex align-items-center justify-content-center cursor-pointer mx-1
            ${e.value === type
                    ? "border-orange bg-white border"
                    : "bg-silver-light"
                  }
            `}
                style={{
                  width: "30%",
                  minHeight: 35,
                  borderRadius: 2,
                  borderWidth: 0.5,
                  marginBottom: 5,
                }}
                onClick={() => onChangeType(e.value)}
              >
                {renderTextInBlock(e)}
              </div>
            ))}
          </div>
        </div>
        <div className="col-12 py-1">
          <span className="text-bold" style={{ fontSize: 12 }}>Pickup type</span>
          {pickupTypeOptions.map((value) => (
            <CheckBoxInput
              key={value}
              name={value}
              text={value === "terminal" ? "Terminal" : "Courier"}
              checked={pickupType == value}
              onChange={() => onChangePickupType(value)}
            />
          ))}
        </div>
        {pickupType !== "terminal" && (
          <div className="col-12 py-1">
            <span className="text-bold" style={{ fontSize: 12 }}>Pickup date</span>
            <DatePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              value={pickupDate}
              onChange={(date) => setPickupDate(date)}
            />
          </div>
        )}

        <div className="col-12">
          <div className="border-bottom py-1">
            <div
              className="d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => collapseBlock(1)}
            >
              <div
                className="text-black font-weight-bold"
                style={{ fontSize: 14, letterSpacing: "0.4px" }}
              >
                Address
              </div>
              {activeKey.includes(1) ? (
                <MdKeyboardArrowUp color="#707070" size={18} />
              ) : (
                <MdKeyboardArrowDown color="#707070" size={18} />
              )}
            </div>
            {activeKey.includes(1) && (
              <RenderAddress
                address={address}
                onChangeAddress={onChangeAddress}
              />
            )}
          </div>
        </div>
        <div className="col-12 ">
          <div className="border-bottom  py-1">
            <div
              className="d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => collapseBlock(2)}
            >
              <div
                className="text-black font-weight-bold"
                style={{ fontSize: 14, letterSpacing: "0.4px" }}
              >
                Information
              </div>
              {activeKey.includes(2) ? (
                <MdKeyboardArrowUp color="#707070" size={18} />
              ) : (
                <MdKeyboardArrowDown color="#707070" size={18} />
              )}
            </div>
            {activeKey.includes(2) && (
              <RenderInformation
                shop_identifier={order_shop?.identifier}
                form_type={form_type}
                order_rows={order_rows}
                orderRowsChange={orderRowsChange}
                type={type}
                delivery={delivery}
                general={general}
                onChangeGeneral={onChangeGeneral}
                errors={errors}
                order={orderMemo}
                country_id={address.country_id}
                cod_details={codDetailsMemo}
                pickup_addresses={pickup_addresses}
              />
            )}
          </div>
        </div>
        <div className="col-12">
          <CheckBoxInput
            name={"noLabel"}
            text={"Do not generate label"}
            checked={isSkipLabel}
            onChange={() => setIsSkipLabel((prev) => !prev)}
          />
        </div>
        <div className="col-12 d-flex justify-content-between mt-2">
          <div style={{ width: "40%" }} />
          <div style={{ width: "40%" }}>
            <ButtonSecondary
              loading={isLoading || loading}
              onClick={createShipping}
            >
              {type == "at_the_store" ? "Create a pickup" : "Create a shipment"}
            </ButtonSecondary>
          </div>
        </div>
      </div>
    </>
  );
}

const RenderInformation = React.memo(({
  shop_identifier,
  order_rows,
  orderRowsChange,
  type,
  delivery,
  general,
  onChangeGeneral,
  errors,
  cod_details,
  country_id,
  form_type,
  pickup_addresses,
  order,
}) => {
  return (
    <div className="shiping_index_smaller">
      <Divs>
        <RowsInShipmentReturn
          form_type={form_type}
          order_rows={order_rows}
          orderRowsChange={orderRowsChange}
          isReturn={true}
        />
        <div className="pb-1">
          <SelectForm
            options={
              shop_identifier == "TCH"
                ? pickup_addresses?.filter(({ technorama }) => technorama)
                : pickup_addresses
            }
            placeholder={"Return address"}
            onlyPlaceholder="Choose"
            selector="suppliers"
            value={general.suppliers}
            onChange={(value) =>
              onChangeGeneral({ target: { value, name: "suppliers" } })
            }
            error={
              errors?.pickup_address &&
              errors?.pickup_address?.map((item) => `${item} `)
            }
          />
        </div>
        <ShipmentAdditionalServices
          order_rows={order_rows}
          cod_details={cod_details}
          order_type={type}
          delivery={delivery}
          variables={general}
          handleChangeText={onChangeGeneral}
          errors={errors}
          country_id={country_id}
          order={order}
        />
      </Divs>
    </div>
  );
});

const RenderAddress = React.memo(({
  address,
  onChangeAddress,
}) => {
  const { countries } = useSelector((state) => state.options);

  return (
    <Divs className="shiping_index_smaller">
      <div className="row">
        <div className="col-6">
          <TextInputModal
            text="Recipient"
            placeholder="Enter"
            name="firstname"
            value={address.firstname}
            onChange={onChangeAddress}
          />
        </div>
        <div className="col-6">
          <TextInputModal
            text="Contact person"
            placeholder="Enter"
            name="lastname"
            value={address.lastname}
            onChange={onChangeAddress}
          />
        </div>
        <div className="col-6">
          <SelectForm
            options={countries?.map((e) => ({
              value: e.id,
              label: e.en_name,
            }))}
            placeholder="Country"
            onlyPlaceholder="Choose"
            value={address.country_id}
            onChange={(value) => {
              onChangeAddress(
                {
                  country_id: value,
                  parcel_terminal_id: null,
                },
                true
              );
            }}
          />
        </div>
        <div className="col-6">
          <TextInputModal
            text="Address"
            placeholder="Enter"
            name="street"
            value={address.street}
            onChange={onChangeAddress}
          />
        </div>
        <div className="col-6">
          <TextInputModal
            text="City"
            placeholder="Enter"
            name="city"
            value={address.city}
            onChange={onChangeAddress}
          />
        </div>
        <div className="col-6">
          <TextInputModal
            text="Postal code"
            placeholder="Enter"
            name="postal_code"
            value={address.postal_code}
            onChange={onChangeAddress}
          />
        </div>
        <div className="col-6">
          <TextInputModal
            text="Phone"
            placeholder="Enter"
            name="phone"
            value={address.phone}
            onChange={onChangeAddress}
          />
        </div>
        <div className="col-6">
          <TextInputModal
            text="Email"
            placeholder="Enter"
            name="other"
            value={address.other}
            onChange={onChangeAddress}
          />
        </div>
        <div className="col-6">
          <TextInputModal
            text="Note from client"
            placeholder="Enter"
            name="comment"
            value={address.comment}
            onChange={onChangeAddress}
          />
        </div>
      </div>
    </Divs>
  );
});

const SuccessCreated = ({ createNewShipping }) => {
  return (
    <div style={{ width: 400 }}>
      <div className="d-flex align-items-center justify-content-center">
        <FcCheckmark />
        <p className="mb-0 ml-2">Successfully created!</p>
        <div className="float-right ml-3">
          <ClassicButton
            loading={false}
            text={"Pildyti naują formą"}
            onClick={() => createNewShipping()}
          />
        </div>
      </div>
    </div>
  );
};
